import { enums } from 'console-kit';
import delay from '@redux-saga/delay-p';
import _ from 'lodash';
import { all, call, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import moment from 'moment-timezone';
import RefData from 'ref-data';

import { getDefaultEditorTabValue, getChannelNextStep, cleanLightboxConfig } from '../../common/utils/publishing-utils';
import navSections from '../../ExperienceWorkspace/common/enums/navigation';
import { publishingWizardModes, displayContainerConstants } from '../../common/enums';
import ConsoleAppActions from '../../App/redux/app-actions';
import { exportEnums } from '../../common/enums';
import { minDurationEffect } from '../../common/redux';
import { validateForm } from '../../common/tools/form-utils';
import { navigateToLegacyPage } from '../../common/utils/legacy-utils';
import ChannelActions from '../channel/channel-actions';
import DisplayContainerActions from '../display-container/display-container-actions';
import DisplayContainerApi from '../display-container/display-container-api';
import DisplayUnitActions from '../display-unit/display-unit-actions';
import ExperienceActions from '../experience/experience-actions';
import ExperienceSelectors from '../experience/experience-selectors';
import SmartUrlActions from '../smart-url/smart-url-actions';
import ExperienceInsightsActions from '../experience-insights/experience-insights-actions';
import ExportTaskActions from '../export-task/export-task-actions';

import {
  DisplayContainer,
  Experience,
  Channel,
  DisplayUnit,
  PublishingDestinations,
  Account,
  SmartUrl,
  PinnedContainer,
  Navigator,
  Winner,
  Rulesets,
} from '..';
import PublishingDestinationsActions from './publishing-destinations-actions';
import PublishingDestinationsApi from './publishing-destinations-api';
import PublishingDestinationsCollection from './publishing-destinations-collection';
import PublishingDestinationsSelectors from './publishing-destinations-selectors';

function* publishHandler({ payload: { displayContainerId } }) {
  const { error, displayContainer } = yield call(
    PublishingDestinationsApi.publishPublishingDestination,
    displayContainerId
  );
  if (error) {
    yield put(PublishingDestinationsActions.updatePublishingDestinationFailure(error));
  } else {
    yield put(DisplayContainerActions.saveDisplayContainer(displayContainer));
  }
}

function* expireHandler({ payload: { displayContainerId } }) {
  const { error, displayContainer } = yield call(
    PublishingDestinationsApi.expirePublishingDestination,
    displayContainerId
  );
  if (error) {
    yield put(PublishingDestinationsActions.updatePublishingDestinationFailure(error));
  } else {
    yield put(DisplayContainerActions.saveDisplayContainer(displayContainer));
  }
}

function* previewHandler({ payload: { publishingDestinationData } }) {
  const destinations = yield select(PublishingDestinationsSelectors.publishingDestinations);
  const destination = PublishingDestinationsCollection.findById(publishingDestinationData, destinations);
  if (destination.displayUnit.isOverlay) {
    yield put(PublishingDestinationsActions.showOverlay(destination.displayContainer));
  } else {
    yield call(window.open, destination.url);
  }
}

function* goToChannelHandler({ payload: { channelId } }) {
  window.location.href = `/admin/channel/show/${channelId}`;
}

/** Optimistically remove the display container and then add it back if it fails */
function* removePublishingDestinationHandler({ payload: { publishingDestinationData } }) {
  const hydratedPublishingDestinationData = JSON.parse(publishingDestinationData);
  const _experience = yield select(ExperienceSelectors.experience);

  // We want to wait before dispatching the success action so the remove animation can play out
  const response = yield minDurationEffect(
    call(PublishingDestinationsApi.removePublishingDestination, publishingDestinationData, _experience.id)
  );
  if (response.error) {
    // The API call failed so reset the client side state to match
    yield put(
      PublishingDestinationsActions.removePublishingDestinationFailure(response.error, publishingDestinationData)
    );
  } else {
    yield put(PublishingDestinationsActions.removePublishingDestinationSuccess(publishingDestinationData));
    yield put(DisplayContainerActions.removeDisplayContainer(hydratedPublishingDestinationData.displayContainerId));
    yield put(SmartUrlActions.removeSmartUrl(hydratedPublishingDestinationData.smartUrlId));
    yield put(ExperienceActions.updateExperienceStatusSuccess(response));
  }
}

function* editPublishingDestinationHandler({ payload: { publishingDestinationData, activeTab } }) {
  yield put(DisplayContainer.Actions.tryFetchThirdPartyProviderCode());
  yield put(Rulesets.Actions.fetchRulesets());

  const publishingDestinations = yield select(PublishingDestinationsSelectors.publishingDestinations);
  let activePublishingDestination = PublishingDestinationsCollection.findById(
    publishingDestinationData,
    publishingDestinations
  );

  yield put(
    PublishingDestinationsActions.showPublishingDestinationEditor(
      activePublishingDestination,
      activeTab || getDefaultEditorTabValue(activePublishingDestination)
    )
  );

  const experience = yield select(ExperienceSelectors.experience);
  const isScrollBarMissed = _.isNil(activePublishingDestination.displayContainer.displayConfig.webEmbed.scrollBar);
  if (activePublishingDestination.isWebEmbed && isScrollBarMissed) {
    const growable = activePublishingDestination.displayContainer.growable;

    yield put(
      PublishingDestinationsActions.updateWebEmbedConfig({
        scrollBar: !growable && !experience.isMicroExperience,
      })
    );
  }

  const { update } = yield race({
    abort:  take(PublishingDestinationsActions.hidePublishingDestinationEditor.getType()),
    update: take(PublishingDestinationsActions.updatePublishingDestination.getType()),
  });

  // on save, update the display container
  if (update) {
    activePublishingDestination = yield select(PublishingDestinationsSelectors.activePublishingDestination);

    const experience = yield select(ExperienceSelectors.experience);

    // Clean the lightbox config before sending to API
    let displayContainerToUpdate = activePublishingDestination.displayContainer;
    if (displayContainerToUpdate.displayConfig && displayContainerToUpdate.displayConfig.lightboxConfig) {
      displayContainerToUpdate = displayContainerToUpdate.setIn(
        ['displayConfig', 'lightboxConfig'],
        cleanLightboxConfig(displayContainerToUpdate.displayConfig.lightboxConfig)
      );
    }

    const response = yield minDurationEffect(
      call(DisplayContainerApi.updateDisplayContainer, displayContainerToUpdate, experience.isMacroExperience)
    );
    if (response.error) {
      yield put(PublishingDestinationsActions.updatePublishingDestinationFailure(response.error));
    } else {
      yield put(DisplayContainerActions.saveDisplayContainer(response));
      yield put(PublishingDestinationsActions.hidePublishingDestinationEditor());
    }
  }
}

//validates and tries to save the pubilshing destination
function* validateAndUpdatePublishingDestinationHandler({ payload: { formId } }) {
  if (
    (yield call(validateForm, { formId })) &&
    (yield call(validateForm, { formId: 'publishing-destination-dimensions' }))
  ) {
    //form is valid, save the publishing destination
    yield put(PublishingDestinationsActions.updatePublishingDestination());
  }
}

function* fetchPublishingDestinationsHandler({ payload: { experienceId } }) {
  const response = yield call(PublishingDestinationsApi.fetchPublishingDestinations, experienceId);
  if (response.error) {
    yield put(PublishingDestinationsActions.fetchPublishingDestinationsFailure(response.error));
  } else {
    const container = _.reduce(
      response,
      (acc, pd) => {
        acc.channels.push(pd.channel);
        acc.displayContainers.push(pd.displayContainer);
        acc.displayUnits.push(pd.displayUnit);
        acc.smartUrls.push(pd.smartUrl);
        return acc;
      },
      { channels: [], displayContainers: [], displayUnits: [], smartUrls: [] }
    );
    yield put(DisplayUnitActions.fetch());
    yield put(ChannelActions.add(container.channels));
    yield put(DisplayContainerActions.add(container.displayContainers));
    yield put(SmartUrlActions.add(container.smartUrls));
    yield put(PublishingDestinationsActions.fetchPublishingDestinationsSuccess(response));
  }
}

function* refreshPublishingDestinationsHandler() {
  const experience = yield select(ExperienceSelectors.experience);
  yield put(PublishingDestinationsActions.fetchPublishingDestinations(experience.id));
}

function* publishingDestinationStatusActionHandler({ payload: { displayContainerId, statusAction } }) {
  const { error, ...response } = yield call(PublishingDestinationsApi.statusAction, displayContainerId, statusAction);
  if (error) {
    yield put(DisplayContainer.Actions.statusActionFailure(error));
  } else {
    yield put(DisplayContainer.Actions.saveDisplayContainer(response));
    yield put(
      ConsoleAppActions.setAlertMessage({
        messageType: enums.messageTypes.SUCCESS,
        header:      'Success',
      })
    );
  }
}

function* savePublishingDestinationHandler() {
  // Experience.
  const { id: experienceId, isReady, isExpired, isMicroExperience } = yield select(Experience.Selectors.experience);

  // Selected Channel.
  const { id: channelId } = yield select(PublishingDestinations.Selectors.selectedChannel);

  // Grabbing the containerTyperef using the selected channel typeRef and the list of supported publishingFormatTypes. (check base-config.js for the full list of supported pusblishingFormatTypes)
  // This has been added as we no longer have the formatType step and we need to figure it out based on the channel selected.
  let { containerType } = yield select(PublishingDestinations.Selectors.selectedFormatType);

  // For innetwork destination types, logic to get containerType is a bit different
  const selectedDestination = yield select(PublishingDestinations.Selectors.selectedDestinationType);
  const isInnetworkDestinationType = yield select(PublishingDestinations.Selectors.isInnetworkDestinationType);
  if (isInnetworkDestinationType) {
    containerType = selectedDestination.containerType;
  }

  // Selected DisplayUnit.
  const selectedDisplayUnit = yield select(PublishingDestinations.Selectors.selectedDisplayUnit);

  // Selected PinnedContainer.
  const selectedPinnedContainer = yield select(PublishingDestinationsSelectors.selectedPinnedContainer);

  // User chosen Dimensions or DisplayUnit defaults
  const { xDimension, yDimension } = yield select(PublishingDestinations.Selectors.dimensions);

  // Schedule dates.
  const publishDate = yield select(PublishingDestinations.Selectors.publishDate);
  const expiryDate = yield select(PublishingDestinations.Selectors.expiryDate);

  // Set display unit.
  let displayUnitId = selectedDisplayUnit.id;

  // Manipulate the dimensions a little
  let width, height, ratio;
  if (isMicroExperience) {
    if (selectedDisplayUnit.isResponsive && selectedDisplayUnit.hasDimensions) {
      ratio = `${xDimension}:${yDimension}`;
    }
    if (!selectedDisplayUnit.isResponsive) {
      width = xDimension;
      height = yDimension;
    }
  } else {
    // reflects Macro logic to keep px dimestions for Responsive DU
    width = xDimension;
    height = yDimension;
  }

  // Select dimensions and container from the pinned container, if the one selected
  let containerId;
  if (selectedPinnedContainer) {
    width = selectedPinnedContainer.width;
    height = selectedPinnedContainer.height;
    ratio = selectedPinnedContainer.ratio;
    containerId = selectedPinnedContainer.id;
    displayUnitId = selectedPinnedContainer.displayUnit.id;
    containerType = null;
  }

  // Privacy policy
  const privacyMgmt = yield select(PublishingDestinations.Selectors.privacyMgmt);
  const privacyConfig = yield select(PublishingDestinations.Selectors.privacyConfig);

  // Display Config
  let displayConfig = {
    lightboxConfig: {},
    webEmbed:       {},
  };

  const lightboxConfig = yield select(PublishingDestinations.Selectors.selectLightboxConfig);
  if (lightboxConfig) {
    displayConfig.lightboxConfig = cleanLightboxConfig({
      ...lightboxConfig,
      transparency: displayContainerConstants.DEFAULT_LIGHTBOX_TRANSPARENCY,
      size:         displayContainerConstants.DEFAULT_OVERLAY_SIZE,
      position:     displayContainerConstants.DEFAULT_OVERLAY_POSITION,
    });

    // Not used in Lightbox
    ratio = null;
    width = null;
    height = null;
  }
  displayConfig = JSON.stringify(displayConfig);

  /**
   * If the experience is in the "ready" or "expired" state we need to both
   *  - create the new publishing destination
   *  - change the status of the experience to "live"
   * Else we can just go ahead and add the destination. The campaign is "scheduled" or "published" and doesn't require a state change.
   * But Staging wizard mode allows only to add a destination, not to publish or change the experience status, so directly go to else
   */
  const isStaging = yield select(PublishingDestinations.Selectors.isStaging);
  if ((isReady || isExpired) && !isStaging) {
    const response = yield call(Experience.Api.publish, experienceId, {
      appName:       undefined,
      channelId,
      displayUnitId,
      containerType,
      publishDate,
      expiryDate,
      width,
      height,
      ratio,
      privacyMgmt,
      displayConfig,
      privacyConfig: !!privacyConfig ? JSON.stringify(privacyConfig) : null,
      containerId,
    });

    if (response.error) yield put(PublishingDestinationsActions.finishDestinationCreationFailure(response.error));

    //We need to refresh the data between the BE and FE after a publishing has happened as we do not know
    //what data from the state has changed in the process. This will trigger an re-request of data.
    //
    //Some of the data that gets requested in the process has to go through the elastic search queue, which means
    //it might take some time until we can actually get it, so here we try to limit the requests we do so that we only
    //refresh the entries if we are already on the refresh page.
    //
    //When we navigate from any other tab/panel to the entries section , the data will be refreshed so no need to sync
    //this data if the publishing happens outside of entries section.
    const curentPanel = yield select(Navigator.Selectors.currentPathPanel);
    if (curentPanel === navSections.ENTRIES) {
      yield put(ExperienceInsightsActions.fetchExperienceTotals(experienceId));
      yield put(ExportTaskActions.fetchLastExport(experienceId, null, exportEnums.exportTypes.PARTICIPANTS));
      yield put(ExportTaskActions.fetchLastExport(experienceId, null, exportEnums.exportTypes.COUPONS));
      yield put(Winner.Actions.fetch(experienceId));
    }

    const publishingDestination = PublishingDestinations.Model.deserialize(response.publishingDestination);
    yield put(PublishingDestinationsActions.finishDestinationCreationSuccess(publishingDestination));
    yield put(Experience.Actions.updateExperienceStatusSuccess(response.experience));
  } else {
    const response = yield minDurationEffect(
      call(PublishingDestinationsApi.createPublishingDestination, experienceId, {
        appName:       undefined,
        channelId,
        displayUnitId,
        containerType,
        width,
        height,
        ratio,
        privacyMgmt,
        displayConfig,
        privacyConfig: !!privacyConfig ? JSON.stringify(privacyConfig) : null,
        containerId,
        isStaging,
      }),
      250
    );
    if (response.error) yield put(PublishingDestinationsActions.finishDestinationCreationFailure(response.error));
    yield put(PublishingDestinationsActions.finishDestinationCreationSuccess(response));
  }
}

function* finishDestinationCreationSuccessHandler({ payload: { publishingDestination } }) {
  yield put(DisplayContainer.Actions.add(publishingDestination.displayContainer));
  yield put(SmartUrl.Actions.add(publishingDestination.smartUrl));
  yield put(PublishingDestinationsActions.createPublishingDestinationSuccess(publishingDestination));

  const publishDate = yield select(PublishingDestinations.Selectors.publishDate);
  const expiryDate = yield select(PublishingDestinations.Selectors.expiryDate);
  const { isLive, isScheduled } = yield select(Experience.Selectors.experience);
  const isStaging = yield select(PublishingDestinations.Selectors.isStaging);
  /**
   * This supports the necessary UI transitions and is closely tied to what happens in the publishing wizard when you click the publish/schedule button.
   * - If you advance to another screen after pressing the button (i.e. go to the rocket launch step) then there is no need to animate the button.
   * - If the button is on the final wizard step then we want to animate the button.
   *
   * These guard conditions are intended to mimic the steps that are provided to the wizard
   *  - If experience is "scheduled", then we don't want to publish (i.e. we don't want to show the rocket)
   *  - If experience is "live", then we don't want to publish it again (i.e. we don't want to show the rocket)
   *  - If the publish || expiry date are set then this indicates that the experience is currently "ready" or "expired" and is being scheduled for later. If the user wants to publish now, these values are undefined
   *  - If wizard is in staging mode, just allow it to close itself
   */
  if (isScheduled || isLive || publishDate || expiryDate || isStaging) {
    yield minDurationEffect(put(PublishingDestinationsActions.savePublishingDestinationSuccess()));
    yield put(PublishingDestinationsActions.finishSaving());
  }
}

function* finishDestinationCreationFailureHandler({ payload: { error } }) {
  yield put(PublishingDestinationsActions.createPublishingDestinationFailure(error));
  const publishDate = yield select(PublishingDestinations.Selectors.publishDate);
  const expiryDate = yield select(PublishingDestinations.Selectors.expiryDate);
  const { isLive, isScheduled } = yield select(Experience.Selectors.experience);
  if (isScheduled || isLive || publishDate || expiryDate) {
    yield minDurationEffect(put(PublishingDestinationsActions.savePublishingDestinationFailure()));
    yield put(PublishingDestinationsActions.finishSaving());
  }
}

function* selectDestinationType({ payload: { id, moveTo, moveNext } }) {
  const isStaging = yield select(PublishingDestinations.Selectors.isStaging);
  const hydratedDestinationType = RefData.Model.hydrate(id);
  const channels = yield select(Channel.Selectors.channels);
  const filteredChannels = Channel.Collection[isStaging ? 'filterByIsStaging' : 'filterByIsLive'](
    Channel.Collection.filterByDestinationType(hydratedDestinationType, channels)
  );
  if (filteredChannels.length === 1) {
    yield put(
      PublishingDestinationsActions.selectChannel(
        filteredChannels[0].id,
        moveTo,
        moveNext,
        hydratedDestinationType,
        false
      )
    );

    const experience = yield select(Experience.Selectors.experience);
    const displayUnits = yield select(DisplayUnit.Selectors.displayUnits);
    // currently pinned containers are not supported for NextGen experiences
    // WYN-7151: remove "experience.isMicroExperience" from this "if" section, when it will be supported
    // ------
    // Innetwork destinations also don't support the pinned containers
    const isInnetworkDestinationType = yield select(PublishingDestinations.Selectors.isInnetworkDestinationType);
    if (experience.isMicroExperience || isInnetworkDestinationType) {
      moveTo(getChannelNextStep(hydratedDestinationType, displayUnits, experience));
    } else {
      // since we already have one channel and it is automatically selected,
      // need to find available pinned containers and only after that define the wizard steps
      yield put(
        PublishingDestinationsActions.fetchPinnedContainersAndMoveNext(
          moveTo,
          hydratedDestinationType,
          filteredChannels[0].id
        )
      );

      moveTo(getChannelNextStep(hydratedDestinationType, displayUnits, experience)); //skipping channel selection
    }
  } else {
    moveNext();
  }
}

//If there is only one display unit for the selected channel then select it
function* selectChannelHandler({ payload: { id, moveNext, moveTo, destinationType, shouldMoveNext } }) {
  const displayUnits = yield select(DisplayUnit.Selectors.displayUnits);
  const experienceFormatTypeId = yield select(PublishingDestinations.Selectors.experienceFormatTypeId);
  const experience = yield select(Experience.Selectors.experience);

  //we grab take all the display units (normally these are available ONLY for webEmbed style experiences) and we grab those that match the destination type
  const displayUnitsForDestinationType = DisplayUnit.Collection.filterByFormatIdAndDestinationType(
    { destinationType, experienceFormatTypeId },
    displayUnits
  );

  //For social stories we need to default the selected display unit to the matching channel selection
  const isInnetworkDestinationType = yield select(PublishingDestinations.Selectors.isInnetworkDestinationType);
  if (isInnetworkDestinationType) {
    //We will always have a display unit associated with a channel (1 to 1) which means this will never return undefined
    const displayUnit = yield select(PublishingDestinations.Selectors.displayUnitForChannelId, id);
    if (displayUnit) {
      //once the display unit matching the channel selection has been found, we set it as default
      yield put(PublishingDestinationsActions.selectDisplayUnit(displayUnit.id));
    }
    moveTo(getChannelNextStep(destinationType, displayUnits, experience));
    // For NextGen, ignore pinned container logic.
    // WYN-7151: Once Pinned containers will be supported in NextGen, just remove this elseif section.
  } else if (experience.isMicroExperience && _.size(displayUnitsForDestinationType) === 1) {
    yield put(PublishingDestinationsActions.selectDisplayUnit(displayUnitsForDestinationType[0].id));
    moveTo(getChannelNextStep(destinationType, displayUnits, experience));
  } else {
    // shouldMoveNext is false, when a step denifition logic has been handled before (for example, in destinationTypeHandler or wizard steps definition)
    // currently pinned containers are not supported for NextGen experiences
    if (shouldMoveNext) {
      yield put(PublishingDestinationsActions.fetchPinnedContainersAndMoveNext(moveTo, destinationType, id));
    }
    const pinnedContainers = yield select(PublishingDestinationsSelectors.pinnedContainersForSelectedFormatType);
    //if we have only one instance in the list we set that value as the default selected one as we auto skip the step in the wizard for the user
    //but still keep the DU step, if there is at least one pinned container
    if (_.size(displayUnitsForDestinationType) === 1 && _.size(pinnedContainers) === 0) {
      yield put(PublishingDestinationsActions.selectDisplayUnit(displayUnitsForDestinationType[0].id));
    }
  }
}

//Will set the default privacy and privacyConfig for the selected display unit
function* _updatePrivacy(selectedDisplayUnit) {
  const account = yield select(Account.Selectors.activeAccount); //defaults are set on the account
  const { microsite, webEmbedLightbox } = account.privacyConfig;

  const defaultPrivacy = [RefData.Enum.TYPE.DISPLAYUNIT_WEB.EMBED, RefData.Enum.TYPE.DISPLAYUNIT_WEB.OVERLAY].includes(
    selectedDisplayUnit.typeRef.id
  )
    ? webEmbedLightbox
    : selectedDisplayUnit.typeRef.id === RefData.Enum.TYPE.DISPLAYUNIT_WEB.MICROSITE
      ? microsite
      : null;

  if (defaultPrivacy) {
    const { privacy, ...config } = defaultPrivacy;
    yield put(PublishingDestinationsActions.selectPrivacyManagement(privacy));
    const privacyConfig = _.isEmpty(_.omit(config, ['provider'])) ? null : config; //means the privacy config isn't fully set on the account, don't use it
    yield put(PublishingDestinationsActions.updatePrivacyManagementConfig(privacyConfig));
  }
}

function* selectDisplayUnitHandler({ payload }) {
  const displayUnits = yield select(DisplayUnit.Selectors.displayUnits);
  const selectedDisplayUnit = DisplayUnit.Collection.findById(payload.id, displayUnits);

  yield call(_updatePrivacy, selectedDisplayUnit);

  const experience = yield select(Experience.Selectors.experience);
  const isInnetworkDestinationType = yield select(PublishingDestinations.Selectors.isInnetworkDestinationType);

  // Reflecting the Macro logic to not show the initial dimesions for responsive DU
  const shouldSetInitialDimestions = !(selectedDisplayUnit.isResponsive && !experience.isMicroExperience);
  if (selectedDisplayUnit.hasDimensions && shouldSetInitialDimestions) {
    yield put(
      PublishingDestinationsActions.setDimensions(selectedDisplayUnit.xDimension, selectedDisplayUnit.yDimension)
    );
    // Also reflecting Macro logic for the Macro Innetwork destinations
  } else if (isInnetworkDestinationType) {
    yield put(
      PublishingDestinationsActions.setDimensions(
        displayContainerConstants.DEFAULT_INNETWORK_X_DIMENSION,
        displayContainerConstants.DEFAULT_INNETWORK_Y_DIMENSION
      )
    );
  }
}

function* startOverWizardHandler() {
  yield call(delay, 250);
  const isStaging = yield select(PublishingDestinations.Selectors.isStaging);
  yield put(PublishingDestinationsActions.resetWizard(isStaging));
}

function* createPublishingDestinationHandler({ payload: { isStaging } }) {
  const experience = yield select(Experience.Selectors.experience);
  /**
   * If the experience isReady and already has a publishDate set then this was set during the campaign creation
   *  && that date is after now then we want to seed the wizard with that data
   * Else the publish date has already passed so don't seed it
   */
  if (experience.isReady && moment().tz('GMT').isBefore(experience.publishDate)) {
    yield put(
      PublishingDestinationsActions.bootstrapWizard(publishingWizardModes.ADD, {
        publishDate: experience.publishDate,
        expiryDate:  experience.expiryDate,
        isStaging,
      })
    );
  } else {
    yield put(
      PublishingDestinationsActions.bootstrapWizard(publishingWizardModes.ADD, {
        isStaging,
      })
    );
  }
}

function* manageChannelsHandler() {
  yield put(PublishingDestinationsActions.cancelAddDestinationWizard());
  yield call(navigateToLegacyPage, '/admin/channel/index');
}

function* bootstrapWizard({ payload: { mode, seedData } }) {
  const experience = yield select(Experience.Selectors.experience);
  const externalRef = experience.externalRef;

  //make sure the channels and display units are loaded in
  for (var Entity of [Channel, DisplayUnit]) {
    const hasFetched = yield select(Entity.Selectors.hasFetched);
    if (!hasFetched) {
      yield put(Entity.Actions.fetch());
    }
  }

  if (!externalRef) {
    yield put(
      ConsoleAppActions.setAlertMessage({
        messageType: enums.messageTypes.ERROR,
        byline:
          'External reference is not specified. Enter value (directly or by adding additional metadata) and continue with publishing.',
      })
    );
  } else if (seedData.isStaging) {
    // no need to check validity errors for staging destination
    yield put(PublishingDestinationsActions.showWizard(mode, seedData));
  } else {
    // checking the validity of the publishing
    const campaignValidityErrorsResponse = yield call(PublishingDestinationsApi.checkCampaignValidity, experience.id);
    if (campaignValidityErrorsResponse && campaignValidityErrorsResponse.error)
      yield put(PublishingDestinationsActions.checkCampaignValidityFailure(campaignValidityErrorsResponse.error));
    if (
      campaignValidityErrorsResponse &&
      campaignValidityErrorsResponse.severity.id === RefData.Enum.STATUS.PUBLISH.ERROR
    ) {
      const { label, resolution } = campaignValidityErrorsResponse;
      const resolutionLabels = resolution.length ? resolution.map((resolution) => resolution.label) : [];
      yield put(
        ConsoleAppActions.setAlertMessage({
          messageType: enums.messageTypes.ERROR,
          byline:      [label, ...resolutionLabels].join(' '),
        })
      );
    } else {
      // if no validity errors, proceed with a wizard
      yield put(PublishingDestinationsActions.showWizard(mode, seedData));
    }
  }
}

function* selectPinnedContainerHandler({ payload: { pinnedContainer } }) {
  // Set up a default privacy management mode for WEB containers
  if (pinnedContainer.typeRef.id === RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.EMBED) {
    yield put(PublishingDestinationsActions.selectPrivacyManagement(RefData.Enum.STRATEGY.PRIVACYMGMT.CUSTOM));
  }
  if (pinnedContainer.typeRef.id === RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.MICROSITE) {
    yield put(PublishingDestinationsActions.selectPrivacyManagement(RefData.Enum.STRATEGY.PRIVACYMGMT.DEFAULT));
  }
}

function* fetchPinnedContainersAndMoveNextHandler({ payload: { moveTo, destinationType, selectedChannelId } }) {
  const displayUnits = yield select(DisplayUnit.Selectors.displayUnits);
  const experience = yield select(Experience.Selectors.experience);

  yield put(PinnedContainer.Actions.fetch(selectedChannelId));
  const { success, failure } = yield race({
    success: take(PinnedContainer.Actions.fetchSuccess),
    failure: take(PinnedContainer.Actions.fetchFailure),
  });
  if (success || failure) {
    const pinnedContainers = yield select(PublishingDestinationsSelectors.pinnedContainersForSelectedFormatType);
    // next wizard step is calculated with taking into account the pinned containers availability
    moveTo(getChannelNextStep(destinationType, displayUnits, experience, pinnedContainers));
  }
}

export default function* publishingDestinationsSagas() {
  yield all([
    takeEvery(PublishingDestinationsActions.publish.getType(), publishHandler),
    takeEvery(PublishingDestinationsActions.expire.getType(), expireHandler),
    takeEvery(PublishingDestinationsActions.preview.getType(), previewHandler),
    takeEvery(PublishingDestinationsActions.goToChannel.getType(), goToChannelHandler),
    takeLatest(PublishingDestinationsActions.editPublishingDestination.getType(), editPublishingDestinationHandler),
    takeEvery(PublishingDestinationsActions.fetchPublishingDestinations.getType(), fetchPublishingDestinationsHandler),
    takeEvery(PublishingDestinationsActions.removePublishingDestination.getType(), removePublishingDestinationHandler),
    takeLatest(ExperienceActions.updateExperienceStatusSuccess.getType(), refreshPublishingDestinationsHandler),
    takeLatest(PublishingDestinationsActions.statusAction.getType(), publishingDestinationStatusActionHandler),
    takeEvery(
      PublishingDestinationsActions.validateAndUpdatePublishingDestination.getType(),
      validateAndUpdatePublishingDestinationHandler
    ),
    takeLatest(PublishingDestinationsActions.selectDestinationType.getType(), selectDestinationType),
    takeLatest(PublishingDestinationsActions.selectChannel.getType(), selectChannelHandler),
    takeEvery(PublishingDestinationsActions.savePublishingDestination.getType(), savePublishingDestinationHandler),
    takeEvery(PublishingDestinationsActions.startOverWizard.getType(), startOverWizardHandler),
    takeLatest(PublishingDestinationsActions.createPublishingDestination.getType(), createPublishingDestinationHandler),
    takeLatest(PublishingDestinationsActions.selectDisplayUnit.getType(), selectDisplayUnitHandler),
    takeLatest(PublishingDestinationsActions.manageChannels.getType(), manageChannelsHandler),
    takeLatest(PublishingDestinationsActions.bootstrapWizard.getType(), bootstrapWizard),
    takeLatest(
      PublishingDestinationsActions.fetchPinnedContainersAndMoveNext.getType(),
      fetchPinnedContainersAndMoveNextHandler
    ),
    takeLatest(PublishingDestinationsActions.selectPinnedContainer.getType(), selectPinnedContainerHandler),
    takeLatest(
      PublishingDestinationsActions.finishDestinationCreationSuccess.getType(),
      finishDestinationCreationSuccessHandler
    ),
    takeLatest(
      PublishingDestinationsActions.finishDestinationCreationFailure.getType(),
      finishDestinationCreationFailureHandler
    ),
  ]);
}
// TODO: remove actions, dselectors, reducers
